// clubSessionNav — pure prev/next navigation over the Club's derived session index.
//
// Mirrors swimNav.ts (the Swim-tab date arrows), but the nav MODEL is sessions,
// not my-workout UUIDs: it steps over the shared mornings produced by
// groupSessions(), keyed on ClubSession.sessionId.
//
// Sessions are DERIVED and re-grouped on every load (offline-first). A late swim
// can shift a session's earliest-start anchor, changing its sessionId, so a
// previously-selected id may vanish; selection resolution tolerates that by
// falling back to the most-recent session (index 0) — never crashes.
//
// The session list is most-recent-first (groupSessions returns it sorted desc by
// startDate): index 0 = latest morning, higher index = older. "prev" = older
// (higher index); "next" = newer (lower index). Stepping "next" onto index 0
// selects null (the most-recent / no-selection sentinel).
import type { ClubSession } from "./clubSessions";

export interface SessionNavState {
  canPrev: boolean;    // an older session exists
  canNext: boolean;    // a newer session exists
  prev: string | null; // sessionId of the older session, or null if none
  next: string | null; // sessionId of the newer session, or null = most-recent; meaningful only when canNext
}

/** Resolve a selected sessionId to its index in a most-recent-first list.
 *  null selection, or an unknown id (a late re-group dropped it), → 0 (most recent). */
function resolveIndex(sessions: ClubSession[], selectedSessionId: string | null): number {
  const found =
    selectedSessionId === null ? 0 : sessions.findIndex((s) => s.sessionId === selectedSessionId);
  return found < 0 ? 0 : found;
}

/** Pure prev/next navigation over the session index. Mirrors swimNavState. */
export function sessionNavState(
  sessions: ClubSession[],
  selectedSessionId: string | null,
): SessionNavState {
  const i = resolveIndex(sessions, selectedSessionId);

  const canPrev = i + 1 < sessions.length;
  const prev = canPrev ? sessions[i + 1].sessionId : null;

  const canNext = i > 0;
  const next = canNext ? (i - 1 === 0 ? null : sessions[i - 1].sessionId) : null;

  return { canPrev, canNext, prev, next };
}

/** The session the header should display: the explicit selection if it still
 *  exists, else the most recent, else null (no sessions). Offline-first: an
 *  unknown selectedSessionId resolves to the most recent, never throws. */
export function currentSession(
  sessions: ClubSession[],
  selectedSessionId: string | null,
): ClubSession | null {
  if (sessions.length === 0) return null;
  return sessions[resolveIndex(sessions, selectedSessionId)];
}
